import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { X, Download, Loader2, Mail, Linkedin, Instagram, MessageCircle, Share2 } from 'lucide-react';
import { useIntl } from 'react-intl';
import { mediaUrl } from '../../../utils/mediaUrl';
import { eventNameFor } from '../../../utils/eventName';
import { CARD_W, CARD_H, renderShareCard } from '../../../utils/shareCard';
import shareTemplate from '../../../assets/speaker_share_template.png';

const BLUE = '#49ABDB';

const Overlay = styled.div`position:fixed;inset:0;background:rgba(15,23,42,.55);display:flex;align-items:flex-start;justify-content:center;overflow-y:auto;padding:2rem 1rem;z-index:1000;`;
const Modal = styled.div`background:#fff;max-width:46rem;width:100%;max-height:90vh;margin:auto 0;display:flex;flex-direction:column;padding:1.75rem;box-shadow:0 25px 60px rgba(15,23,42,.35);position:relative;`;
const CloseBtn = styled.button`position:absolute;top:1rem;right:1rem;border:0;background:transparent;color:#5B6B78;cursor:pointer;padding:.25rem;&:hover{color:#111D4E;}`;
const ModalTitle = styled.h2`margin:0 0 .35rem;font-size:1.3rem;color:#111D4E;`;
const ModalHint = styled.p`margin:0 0 1.25rem;color:#5B6B78;font-size:.88rem;`;

const Body = styled.div`display:flex;gap:1.75rem;flex-wrap:wrap;flex:1;min-height:0;overflow-y:auto;@media(max-width:600px){flex-direction:column;}`;
const PreviewWrap = styled.div`flex:none;width:320px;@media(max-width:600px){width:100%;max-width:320px;margin:0 auto;}`;
const PreviewFrame = styled.div`position:relative;width:100%;aspect-ratio:${CARD_W}/${CARD_H};background:#eef2f7;box-shadow:0 10px 30px rgba(15,23,42,.18);overflow:hidden;`;
const PreviewCanvas = styled.canvas`width:100%;height:100%;display:block;`;
const PreviewOverlay = styled.div`position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.7);color:#5B6B78;font-size:.85rem;gap:.4rem;`;
const Spin = styled(Loader2)`animation:spin 1s linear infinite;@keyframes spin{to{transform:rotate(360deg);}}`;

const Fields = styled.div`flex:1;min-width:240px;display:flex;flex-direction:column;gap:1rem;`;
const FieldLabel = styled.label`display:block;font-size:.78rem;font-weight:700;color:#5B6B78;margin-bottom:.35rem;`;
const QuoteInput = styled.textarea`width:100%;resize:vertical;min-height:4.2rem;padding:.6rem .7rem;border:1px solid #cbd5e1;font:inherit;font-size:.9rem;`;
const EntrySelect = styled.select`width:100%;padding:.55rem 2rem .55rem .7rem;border:1px solid #cbd5e1;background:#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235B6B78' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E") no-repeat right .7rem center;appearance:none;color:#111D4E;font:inherit;font-size:.9rem;font-weight:600;cursor:pointer;&:hover{border-color:${BLUE};}&:focus{outline:2px solid ${BLUE};outline-offset:1px;}`;
const EntryHint = styled.div`margin-top:.3rem;color:#5B6B78;font-size:.76rem;`;
const ReadonlyLine = styled.div`padding:.55rem .7rem;background:#f8fafc;border:1px solid #e2e8f0;color:#334155;font-size:.9rem;`;
const PhotoHint = styled.div`background:#fff7ed;border-left:3px solid #f59e0b;color:#92400e;padding:.55rem .7rem;font-size:.8rem;line-height:1.45;margin-top:-.35rem;`;
const ErrorMsg = styled.div`font-size:.8rem;color:#b91c1c;`;
const Actions = styled.div`display:flex;gap:.6rem;margin-top:.25rem;`;
const DownloadBtn = styled.button`display:inline-flex;align-items:center;gap:.4rem;border:0;padding:.65rem 1.1rem;background:${BLUE};color:#fff;font-weight:700;font-size:.9rem;cursor:pointer;transition:background-color .3s;&:hover:not(:disabled){background:#0056b3;}&:disabled{opacity:.5;cursor:not-allowed;}`;
const ShareBlock = styled.div`display:flex;flex-direction:column;gap:.5rem;margin-top:.25rem;`;
const ShareOptions = styled.div`display:flex;flex-wrap:wrap;gap:.45rem;`;
const optionStyle = `display:inline-flex;align-items:center;gap:.35rem;border:1px solid #cbd5e1;background:#fff;color:#111D4E;padding:.5rem .7rem;font:inherit;font-size:.82rem;font-weight:700;cursor:pointer;text-decoration:none;&:hover:not(:disabled){background:#f8fafc;border-color:${BLUE};}&:disabled{opacity:.5;cursor:not-allowed;}`;
const ShareLink = styled.a`${optionStyle}`;
const ShareButton = styled.button`${optionStyle}`;
const ShareHelp = styled.p`margin:0;color:#5B6B78;font-size:.76rem;line-height:1.45;`;
const ShareNotice = styled.div`font-size:.8rem;color:#111D4E;background:#eff6ff;border-left:3px solid ${BLUE};padding:.5rem .7rem;line-height:1.45;`;

// A speaker's photo may be an external URL from AI research/social profiles
// with no CORS headers (see mediaUrl.js) - drawing that onto the canvas
// still renders a preview fine, but taints the canvas so toBlob/toDataURL
// throw. That failure is caught and surfaced as fallback, not a hard error:
// the card still renders (with an initials avatar in place of the photo),
// so the speaker can still download something rather than nothing.
// What the card prints for an engagement: the title, and the day with the speaker's own slot when the
// program gives one (a talk is much shorter than its session). The day is the entry's own (`entryDate`,
// a multi-day event's talks fall on different days), else the event's.
export const slotOf = (engagement, intl, roleLabel) => {
  const talkTitle = engagement.talkTitle || engagement.talkTitleSuggestion || engagement.sessionTitle || roleLabel(engagement.role);
  const eventDate = engagement.eventDateTime ? new Date(engagement.eventDateTime) : null;
  const day = engagement.entryDate ? new Date(`${engagement.entryDate}T12:00:00`) : eventDate;
  const dateStr = day ? day.toLocaleDateString(intl.locale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  const slotStart = engagement.talkStartTime || engagement.sessionStartTime;
  const slotEnd = engagement.talkStartTime ? engagement.talkEndTime : engagement.sessionEndTime;
  const timeStr = slotStart
    ? `${slotStart}${slotEnd ? `–${slotEnd}` : ''}`
    : (eventDate ? eventDate.toLocaleTimeString(intl.locale, { hour: '2-digit', minute: '2-digit' }) : '');
  return { talkTitle, talkTime: [dateStr, timeStr].filter(Boolean).join(' · ') };
};

// `choices`: the person's other confirmed engagements at the same event (this one among them). With
// more than one, the title line is a picker: `onChoose(id)` switches the engagement, the preview is
// drawn again and the note stays.
const ShareEngagementModal = ({ engagement, identity, ownProfile = false, shareUrl = null, choices = [], onChoose, onClose }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const canvasRef = useRef(null);
  // Prefilled from the speaker's own "why I'm joining HEARTS" motivation
  // (same field AttendeeProfile.js shows under MotivationLabel) rather than
  // starting blank - it's already personal, on-topic copy the speaker wrote
  // themselves, and they can still edit or clear it here. The card shrinks and
  // wraps the text instead of capping the input.
  const [quote, setQuote] = useState(() => identity.motivation || '');
  const [rendering, setRendering] = useState(true);
  const [photoFallback, setPhotoFallback] = useState(false);
  const [downloadBlocked, setDownloadBlocked] = useState(false);
  const [shareNotice, setShareNotice] = useState('');
  const [error, setError] = useState('');

  const roleLabel = (r) => t(`speaker.engagementRole.${r}`, r ? r.charAt(0) + r.slice(1).toLowerCase() : r);

  const { talkTitle, talkTime } = slotOf(engagement, intl, roleLabel);
  const picking = choices.length > 1 && typeof onChoose === 'function';

  const subtitle = [identity.institution, [identity.city, identity.country].filter(Boolean).join(', ')].filter(Boolean).join(' / ');

  const eventLabel = eventNameFor(intl.locale, engagement.eventName, engagement.eventNameEN);
  const resolvedShareUrl = shareUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  const shareSubject = t('speaker.share.emailSubject', 'My HEARTS engagement: {talk}', { talk: talkTitle });
  const shareText = [
    t('speaker.share.shareText', 'I am speaking at {event}: {talk} ({time}).', {
      event: eventLabel,
      talk: talkTitle,
      time: talkTime || t('speaker.share.talkTimeUnset', 'To be announced'),
    }),
    quote ? `“${quote}”` : null,
    resolvedShareUrl,
  ].filter(Boolean).join('\n\n');
  const mailtoHref = `mailto:?subject=${encodeURIComponent(shareSubject)}&body=${encodeURIComponent(shareText)}`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(resolvedShareUrl)}`;
  // The browser can hand the PNG file itself to the operating system's share sheet (and so to
  // Instagram, WhatsApp, mail or LinkedIn) only where Web Share Level 2 is supported. The platform
  // links below share text and a link; they cannot attach a local file.
  const canShareFiles = (() => {
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
    try {
      return navigator.canShare({ files: [new File([''], 'hearts-card.png', { type: 'image/png' })] });
    } catch (e) {
      return false;
    }
  })();

  useEffect(() => {
    let cancelled = false;
    setRendering(true);
    setError('');
    setPhotoFallback(false);
    renderShareCard(canvasRef.current, shareTemplate, {
      photoUrl: identity.photoUrl ? mediaUrl(identity.photoUrl) : null,
      name: identity.name,
      subtitle,
      quote,
      talkTitle,
      talkTime,
    })
      .then((result) => {
        if (!cancelled) {
          // Only a picture that was on file can fall back; a profile without one is not a failure.
          setPhotoFallback(Boolean(identity.photoUrl) && !(result && result.photoDrawn));
        }
      })
      .catch(() => { if (!cancelled) setError(t('speaker.share.renderError', 'The preview could not be generated.')); })
      .finally(() => { if (!cancelled) setRendering(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, identity.photoUrl, identity.name, subtitle, talkTitle, talkTime]);

  const shareFileName = () => {
    const slug = (engagement.eventNameEN || engagement.eventName || 'hearts-engagement').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return `hearts-${slug || 'share'}.png`;
  };

  const exportCard = () => new Promise((resolve) => {
    try {
      canvasRef.current.toBlob((blob) => resolve(blob), 'image/png');
    } catch (e) {
      resolve(null);
    }
  });

  const saveBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = shareFileName();
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const download = async () => {
    setDownloadBlocked(false);
    const blob = await exportCard();
    if (!blob) { setDownloadBlocked(true); return; }
    saveBlob(blob);
  };

  const shareImage = async () => {
    setDownloadBlocked(false);
    setShareNotice('');
    const blob = await exportCard();
    if (!blob) { setDownloadBlocked(true); return; }
    const file = new File([blob], shareFileName(), { type: 'image/png' });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: shareSubject, text: shareText });
        return;
      }
      saveBlob(blob);
      setShareNotice(t('speaker.share.nativeUnsupported', 'This browser cannot hand the image to an app. The image was downloaded; attach it in the app.'));
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      saveBlob(blob);
      setShareNotice(t('speaker.share.shareFailed', 'The image could not be shared. It was downloaded instead; attach it in the app.'));
    }
  };

  const instagram = async () => {
    if (canShareFiles) { await shareImage(); return; }
    setDownloadBlocked(false);
    setShareNotice('');
    const blob = await exportCard();
    if (!blob) { setDownloadBlocked(true); return; }
    saveBlob(blob);
    setShareNotice(t('speaker.share.instagramNoLink', 'Instagram has no web share link. The image was downloaded; post it from the Instagram app.'));
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseBtn type="button" onClick={onClose} aria-label={t('speaker.share.close', 'Close')}><X size={20} /></CloseBtn>
        <ModalTitle>{t('speaker.share.title', 'Share your HEARTS engagement')}</ModalTitle>
        <ModalHint>{t('speaker.share.hint', 'Download an image with your photo and details for {eventName} to post on social media.', { eventName: eventLabel })}</ModalHint>
        <Body>
          <PreviewWrap>
            <PreviewFrame>
              <PreviewCanvas ref={canvasRef} width={CARD_W} height={CARD_H} />
              {rendering && <PreviewOverlay><Spin size={16} />{t('speaker.share.rendering', 'Rendering…')}</PreviewOverlay>}
            </PreviewFrame>
          </PreviewWrap>
          <Fields>
            <div>
              <FieldLabel>{t('speaker.share.quoteLabel', 'A short personal note (optional)')}</FieldLabel>
              <QuoteInput
                value={quote}
                placeholder={t('speaker.share.quotePlaceholder', 'Why I am joining HEARTS…')}
                onChange={(e) => setQuote(e.target.value)}
              />
            </div>
            {picking ? (
              <div>
                <FieldLabel htmlFor="share-entry">{t('speaker.share.entryLabel', 'Program entry')}</FieldLabel>
                <EntrySelect id="share-entry" value={engagement.id} onChange={(e) => onChoose(e.target.value)}>
                  {choices.map((choice) => {
                    const slot = slotOf(choice, intl, roleLabel);
                    const role = choice.role === 'SPEAKER' ? null : roleLabel(choice.role);
                    return (
                      <option key={choice.id} value={choice.id}>
                        {[slot.talkTitle, role, slot.talkTime].filter(Boolean).join(' · ')}
                      </option>
                    );
                  })}
                </EntrySelect>
                <EntryHint>{t('speaker.share.entryHint', 'You have {count} engagements at this event – pick the one this image is for.', { count: choices.length })}</EntryHint>
              </div>
            ) : (
              <div>
                <FieldLabel>{t('speaker.share.talkTitleLabel', 'Talk title')}</FieldLabel>
                <ReadonlyLine>{talkTitle}</ReadonlyLine>
              </div>
            )}
            <div>
              <FieldLabel>{t('speaker.share.talkTimeLabel', 'Date & time')}</FieldLabel>
              <ReadonlyLine>{talkTime || t('speaker.share.talkTimeUnset', 'To be announced')}</ReadonlyLine>
            </div>
            {photoFallback && (
              <PhotoHint role="status">
                {ownProfile
                  ? t('speaker.share.photoFallbackOwn', 'Your profile picture could not be added to this image. Upload the picture again on your profile (JPG, PNG or WebP) and download the image again.')
                  : t('speaker.share.photoFallback', 'The profile picture could not be added to this image. The person needs to upload the picture again on their profile.')}
              </PhotoHint>
            )}
            {error && <ErrorMsg>{error}</ErrorMsg>}
            {downloadBlocked && (
              <ErrorMsg>{t('speaker.share.downloadBlocked', "Your image couldn't be exported, likely because of your profile photo's source. Try again after re-uploading your photo on your profile.")}</ErrorMsg>
            )}
            <ShareBlock>
              <FieldLabel>{t('speaker.share.optionsLabel', 'Share directly')}</FieldLabel>
              <ShareOptions>
                {canShareFiles && (
                  <ShareButton type="button" onClick={shareImage} disabled={rendering || Boolean(error)}>
                    <Share2 size={15} />{t('speaker.share.shareImage', 'Share image…')}
                  </ShareButton>
                )}
                <ShareLink href={mailtoHref}>
                  <Mail size={15} />{t('speaker.share.email', 'Email')}
                </ShareLink>
                <ShareLink href={whatsappHref} target="_blank" rel="noopener noreferrer">
                  <MessageCircle size={15} />{t('speaker.share.whatsapp', 'WhatsApp')}
                </ShareLink>
                <ShareLink href={linkedinHref} target="_blank" rel="noopener noreferrer">
                  <Linkedin size={15} />{t('speaker.share.linkedin', 'LinkedIn')}
                </ShareLink>
                <ShareButton type="button" onClick={instagram} disabled={rendering || Boolean(error)}>
                  <Instagram size={15} />{t('speaker.share.instagram', 'Instagram')}
                </ShareButton>
              </ShareOptions>
              <ShareHelp>
                {canShareFiles
                  ? t('speaker.share.helpNative', 'Email and WhatsApp open with your text and a link; "Share image…" hands the PNG to the app you pick. LinkedIn shares the link. Instagram opens the share sheet.')
                  : t('speaker.share.help', 'Email and WhatsApp open with your text and a link; attach the downloaded image there. LinkedIn shares the link. Instagram has no web share link, so the image is downloaded for you to post.')}
              </ShareHelp>
              {shareNotice && <ShareNotice role="status">{shareNotice}</ShareNotice>}
            </ShareBlock>
            <Actions>
              <DownloadBtn type="button" onClick={download} disabled={rendering || Boolean(error)}>
                <Download size={16} />{t('speaker.share.download', 'Download image')}
              </DownloadBtn>
            </Actions>
          </Fields>
        </Body>
      </Modal>
    </Overlay>
  );
};

export default ShareEngagementModal;
